import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useLocation, useNavigate } from "react-router-dom"
import { AnimatePresence, motion } from "motion/react"
import { unpaidOrderStore } from "../store/unpaidOrderStore"
import { isUnpaidTicketNoticePath } from "../lib/unpaidTicketRoutes"
import { formatSessionDateTime, isUnpaid } from "../features/tickets/ticketUtils"
import PaymentHoldBanner from "./PaymentHoldBanner"
import UnpaidOrderPayment from "../features/tickets/UnpaidOrderPayment"
import CancelOrderDialog, { CancelOrderDialogHandle } from "./CancelOrderDialog"
import SwipeSheetPanel from "./SwipeSheetPanel"
import { useRequest } from "../hooks/useRequest"
import { fetchUnpaidTicket } from "../hooks/useUnpaidOrderPoll"
import { clearOrderPaymentMethod } from "../lib/orderPaymentMethodStorage"
import { userStore } from "../store/userStore"

const BODY = "'Manrope', sans-serif"
const MONO = "'JetBrains Mono', monospace"
const DISPLAY = "'Tilda Sans', sans-serif"

const POPUP_SHEET_CLASS =
    "relative flex w-full max-h-[90dvh] flex-col overflow-hidden rounded-t-[28px] md:h-auto md:max-h-[min(92svh,820px)] md:w-full md:max-w-[428px] md:rounded-[36px]"

export default function UnpaidTicketNotice() {
    const { pathname } = useLocation()
    const navigate = useNavigate()
    const { request, requestData } = useRequest()
    const user = userStore(state => state.user)
    const unpaidTicket = unpaidOrderStore(state => state.unpaidTicket)
    const noticeView = unpaidOrderStore(state => state.noticeView)
    const markSeen = unpaidOrderStore(state => state.markSeen)
    const setUnpaidTicket = unpaidOrderStore(state => state.setUnpaidTicket)
    const openNotice = unpaidOrderStore(state => state.openNotice)
    const minimizeNotice = unpaidOrderStore(state => state.minimizeNotice)

    const [cancelLoading, setCancelLoading] = useState(false)
    const cancelRef = useRef<CancelOrderDialogHandle>(null)
    const lastAutoTicketId = useRef<string | null>(null)

    const hasUnpaid = !!user && !!unpaidTicket && isUnpaid(unpaidTicket)
    const sheetOpen = hasUnpaid && noticeView === "open"
    // На «Мои билеты» заказ уже на экране — чип не дублируем
    const chipVisible = hasUnpaid && noticeView === "minimized" && pathname !== "/tickets"

    // Автопоказ: на афише — sheet; на прочих страницах — плавающий чип
    useEffect(() => {
        if (!hasUnpaid || !unpaidTicket) {
            lastAutoTicketId.current = null
            return
        }

        const isNewTicket = lastAutoTicketId.current !== unpaidTicket.id
        if (isNewTicket) lastAutoTicketId.current = unpaidTicket.id

        if (noticeView !== "closed" && !isNewTicket) return

        if (isUnpaidTicketNoticePath(pathname)) {
            openNotice()
        } else {
            minimizeNotice()
        }
    }, [hasUnpaid, unpaidTicket?.id, pathname, noticeView, openNotice, minimizeNotice, unpaidTicket])

    const refresh = async () => {
        const ticket = await fetchUnpaidTicket(requestData)
        setUnpaidTicket(ticket)
    }

    const minimize = () => {
        if (unpaidTicket) markSeen(unpaidTicket.id)
        minimizeNotice()
    }

    const handleCancel = () => {
        if (!unpaidTicket) return
        markSeen(unpaidTicket.id)
        cancelRef.current?.open({
            unpaid: true,
            onCancel: async () => {
                setCancelLoading(true)
                try {
                    await request("DELETE", `user/order/${unpaidTicket.id}`)
                    clearOrderPaymentMethod(unpaidTicket.id)
                    await refresh()
                } finally {
                    setCancelLoading(false)
                }
            },
        })
    }

    const handleGoToTickets = () => {
        if (!unpaidTicket) return
        markSeen(unpaidTicket.id)
        minimizeNotice()
        navigate("/tickets", { state: { highlightOrderId: unpaidTicket.id } })
    }

    if (!hasUnpaid || !unpaidTicket) return null

    const session = formatSessionDateTime(unpaidTicket.session.datetime_at)
    const poster = unpaidTicket.film.poster_mobile?.big

    return createPortal(
        <>
            {sheetOpen && (
                <div
                    className="fixed inset-0 z-[140] flex items-end justify-center overflow-hidden md:items-center md:px-4"
                    style={{ background: "rgba(10,18,42,0.8)", backdropFilter: "blur(6px)" }}
                    onMouseDown={e => {
                        if (e.target === e.currentTarget) minimize()
                    }}>
                    <SwipeSheetPanel
                        onDismiss={minimize}
                        className={POPUP_SHEET_CLASS}
                        style={{
                            background: "linear-gradient(160deg, #1a2d55 0%, #18133b 100%)",
                            border: "1.5px solid rgba(255,255,255,0.12)",
                        }}
                        motionProps={{
                            initial: { y: "100%", opacity: 0 },
                            animate: { y: 0, opacity: 1 },
                            exit: { y: "100%", opacity: 0 },
                            transition: { type: "spring", stiffness: 320, damping: 32 },
                            onMouseDown: e => e.stopPropagation(),
                        }}>
                        <div className="flex min-h-0 w-full flex-1 flex-col gap-5 overflow-y-auto px-5 pb-[max(1.25rem,var(--safe-bottom))] pt-1 md:gap-6 md:px-10 md:py-8">
                            <div className="flex w-full flex-col items-center gap-4">
                                <div className="flex w-full items-center justify-center gap-2">
                                    <span
                                        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] uppercase tracking-[0.06em]"
                                        style={{
                                            fontFamily: MONO,
                                            fontWeight: 700,
                                            color: "#f5a623",
                                            background: "rgba(245,166,35,0.12)",
                                            border: "1px solid rgba(245,166,35,0.35)",
                                        }}>
                                        <span
                                            className="size-2 rounded-full bg-[#349be6]"
                                            style={{ boxShadow: "0 0 6px rgba(52,155,230,0.8)" }}
                                        />
                                        Неоплаченный билет
                                    </span>
                                </div>

                                <h2
                                    className="w-full text-center text-[22px] leading-tight font-bold text-white md:text-[24px]"
                                    style={{ fontFamily: DISPLAY, letterSpacing: "-0.02em" }}>
                                    {unpaidTicket.film.name}
                                </h2>

                                <p
                                    className="w-full text-center text-[15px] leading-relaxed text-white/70"
                                    style={{ fontFamily: BODY }}>
                                    {session.full}
                                </p>

                                {poster && (
                                    <div className="relative w-full overflow-hidden rounded-[18px]">
                                        <img src={poster} alt="" className="aspect-[16/9] w-full object-cover" />
                                        <div
                                            className="pointer-events-none absolute inset-0"
                                            style={{
                                                background:
                                                    "linear-gradient(to top, rgba(10,18,42,0.85) 0%, transparent 55%)",
                                            }}
                                        />
                                    </div>
                                )}

                                <PaymentHoldBanner
                                    createdAt={unpaidTicket.created_at}
                                    expiredAt={unpaidTicket.expired_at}
                                    className="w-full"
                                />
                            </div>

                            <div className="w-full" data-sheet-drag-exempt>
                                <UnpaidOrderPayment ticket={unpaidTicket} onUpdated={refresh} />
                            </div>

                            <div className="flex w-full shrink-0 flex-col gap-3">
                                <button
                                    type="button"
                                    disabled={cancelLoading}
                                    onClick={handleCancel}
                                    className="flex h-11 w-full cursor-pointer items-center justify-center rounded-full border border-white/12 bg-transparent px-8 text-[14px] text-[#e4206b] transition-all hover:bg-[#e4206b]/10 disabled:opacity-50"
                                    style={{ fontFamily: BODY, fontWeight: 600 }}>
                                    {cancelLoading ? "Отмена..." : "Отменить заказ"}
                                </button>

                                <button
                                    type="button"
                                    onClick={handleGoToTickets}
                                    className="w-full py-1 text-center text-[13px] text-[#349be6] transition-opacity hover:opacity-80"
                                    style={{ fontFamily: MONO, fontWeight: 700 }}>
                                    Открыть в «Мои билеты» →
                                </button>
                            </div>
                        </div>
                    </SwipeSheetPanel>
                </div>
            )}

            <AnimatePresence>
                {chipVisible && (
                    <motion.button
                        key="unpaid-chip"
                        type="button"
                        initial={{ opacity: 0, x: 40 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 40 }}
                        transition={{ type: "spring", stiffness: 320, damping: 28 }}
                        whileTap={{ scale: 0.96 }}
                        onClick={() => openNotice()}
                        aria-label="Открыть неоплаченный заказ"
                        className="fixed right-0 z-[900] flex max-w-[220px] cursor-pointer items-center gap-2.5 rounded-l-[18px] border border-r-0 border-white/15 py-2.5 pr-3 pl-2.5 shadow-[0_8px_28px_rgba(0,0,0,0.45)] bottom-[calc(max(12px,var(--safe-bottom))+88px)] md:bottom-8"
                        style={{
                            background: "linear-gradient(160deg, #1a2d55 0%, #18133b 100%)",
                        }}>
                        {poster ? (
                            <img
                                src={poster}
                                alt=""
                                className="size-11 shrink-0 rounded-[12px] object-cover"
                            />
                        ) : (
                            <div
                                className="flex size-11 shrink-0 items-center justify-center rounded-[12px]"
                                style={{ background: "rgba(245,166,35,0.15)" }}>
                                <span
                                    className="size-2.5 rounded-full bg-[#f5a623]"
                                    style={{ boxShadow: "0 0 8px rgba(245,166,35,0.8)" }}
                                />
                            </div>
                        )}
                        <div className="min-w-0 flex-1 text-left">
                            <p
                                className="truncate text-[10px] uppercase tracking-[0.06em] text-[#f5a623]"
                                style={{ fontFamily: MONO, fontWeight: 700 }}>
                                Не оплачен
                            </p>
                            <p
                                className="truncate text-[13px] leading-tight text-white"
                                style={{ fontFamily: BODY, fontWeight: 600 }}>
                                {unpaidTicket.film.name}
                            </p>
                        </div>
                    </motion.button>
                )}
            </AnimatePresence>

            <CancelOrderDialog ref={cancelRef} />
        </>,
        document.body,
    )
}
